import '@fontsource/chivo/300.css';
import '@fontsource/chivo/400.css';
import '@fontsource/chivo/700.css';
import '@fontsource/chivo/900.css';
import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WebSocketProvider } from "@/context/WebSocketContext";
import { ThemeProvider } from "@/context/ThemeContext";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import DevicesPage from "@/pages/DevicesPage";
import DeviceDetailPage from "@/pages/DeviceDetailPage";
import EventsPage from "@/pages/EventsPage";
import SchoolsPage from "@/pages/SchoolsPage";
import RoomsPage from "@/pages/RoomsPage";
import AlarmProfilesPage from "@/pages/AlarmProfilesPage";
import NotificationRulesPage from "@/pages/NotificationRulesPage";
import SettingsPage from "@/pages/SettingsPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import UsersPage from "@/pages/UsersPage";
import AirQualityPage from "@/pages/AirQualityPage";
import OnboardingGuidePage from "@/pages/OnboardingGuidePage";
import StaffAlarmView from "@/pages/StaffAlarmView";
import IncidentsPage from "@/pages/IncidentsPage";
import DocumentsPage from "@/pages/DocumentsPage";
import ImportSensorsPage from "@/pages/ImportSensorsPage";
import KommunDashboardPage from "@/pages/KommunDashboardPage";
import AdminGuidePage from "@/pages/AdminGuidePage";
import AlarmHistoryPage from "@/pages/AlarmHistoryPage";
import KnowledgeBasePage from "@/pages/KnowledgeBasePage";
import KnowledgeDocumentPage from "@/pages/KnowledgeDocumentPage";
import KnowledgeAdminPage from "@/pages/KnowledgeAdminPage";
import CustomerOnboardingPage from "@/pages/CustomerOnboardingPage";
import OnboardingSubmissionsPage from "@/pages/OnboardingSubmissionsPage";
import SensorLinkingPage from "@/pages/SensorLinkingPage";
import UnlinkedSensorsPage from "@/pages/UnlinkedSensorsPage";
import TestLabPage from "@/pages/TestLabPage";
import SalesPortalPage from "@/pages/SalesPortalPage";
import QuoteSignPage from "@/pages/QuoteSignPage";
import ReportsPage from "@/pages/ReportsPage";
import LeadsPage from "@/pages/LeadsPage";
import AcademyPage from "@/pages/AcademyPage";
import AcademyLessonPage from "@/pages/AcademyLessonPage";
import { AcademyProvider } from "@/components/academy/AcademyProvider";

// Layout
import MainLayout from "@/components/layout/MainLayout";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-600">Loading...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <WebSocketProvider>{children}</WebSocketProvider>;
};

// Admin-only route wrapper
const AdminRoute = ({ children }) => {
  const { canCreate } = useAuth();
  if (!canCreate()) return <Navigate to="/" replace />;
  return children;
};

// Superadmin-only route wrapper
const SuperAdminRoute = ({ children }) => {
  const { isSuperAdmin } = useAuth();
  if (!isSuperAdmin()) return <Navigate to="/" replace />;
  return children;
};

// Role-based index page
const IndexPage = () => {
  const { isStaff, isKommunAdmin } = useAuth();
  if (isStaff()) return <StaffAlarmView />;
  if (isKommunAdmin()) return <KommunDashboardPage />;
  return <DashboardPage />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/onboard/:token" element={<CustomerOnboardingPage />} />
              <Route path="/quote/:token" element={<QuoteSignPage />} />
              
              {/* Protected Routes */}
              <Route path="/" element={
                <ProtectedRoute>
                  <AcademyProvider>
                    <MainLayout />
                  </AcademyProvider>
                </ProtectedRoute>
              }>
                <Route index element={<IndexPage />} />
                <Route path="devices" element={<DevicesPage />} />
                <Route path="devices/:deviceId" element={<DeviceDetailPage />} />
                <Route path="events" element={<EventsPage />} />
                <Route path="alarm-history" element={<AlarmHistoryPage />} />
                <Route path="incidents" element={<IncidentsPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="schools" element={<SuperAdminRoute><SchoolsPage /></SuperAdminRoute>} />
                <Route path="rooms" element={<SuperAdminRoute><RoomsPage /></SuperAdminRoute>} />
                <Route path="alarm-profiles" element={<SuperAdminRoute><AlarmProfilesPage /></SuperAdminRoute>} />
                <Route path="notification-rules" element={<SuperAdminRoute><NotificationRulesPage /></SuperAdminRoute>} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="air-quality" element={<AirQualityPage />} />
                <Route path="users" element={<AdminRoute><UsersPage /></AdminRoute>} />
                <Route path="onboarding" element={<SuperAdminRoute><OnboardingGuidePage /></SuperAdminRoute>} />
                <Route path="documents" element={<DocumentsPage />} />
                <Route path="import" element={<SuperAdminRoute><ImportSensorsPage /></SuperAdminRoute>} />
                <Route path="admin-guide" element={<SuperAdminRoute><AdminGuidePage /></SuperAdminRoute>} />
                <Route path="kommun" element={<KommunDashboardPage />} />
                <Route path="settings" element={<SuperAdminRoute><SettingsPage /></SuperAdminRoute>} />
                <Route path="academy" element={<AcademyPage />} />
                <Route path="academy/:lessonId" element={<AcademyLessonPage />} />
                <Route path="knowledge" element={<KnowledgeBasePage />} />
                <Route path="knowledge/:slug" element={<KnowledgeDocumentPage />} />
                <Route path="knowledge/admin" element={<SuperAdminRoute><KnowledgeAdminPage /></SuperAdminRoute>} />
                <Route path="onboarding-submissions" element={<SuperAdminRoute><OnboardingSubmissionsPage /></SuperAdminRoute>} />
                <Route path="sensor-linking" element={<SuperAdminRoute><SensorLinkingPage /></SuperAdminRoute>} />
                <Route path="unlinked-sensors" element={<SuperAdminRoute><UnlinkedSensorsPage /></SuperAdminRoute>} />
                <Route path="test-lab" element={<SuperAdminRoute><TestLabPage /></SuperAdminRoute>} />
                <Route path="sales" element={<SuperAdminRoute><SalesPortalPage /></SuperAdminRoute>} />
                <Route path="reports" element={<AdminRoute><ReportsPage /></AdminRoute>} />
                <Route path="leads" element={<SuperAdminRoute><LeadsPage /></SuperAdminRoute>} />
              </Route>
              
              {/* Catch-all: redirect to login */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" />
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
